import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, MoreVertical, Pencil, Trash2, Users, Eye, Phone, Mail, MapPin, Building2, ArrowUpDown, Upload, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useSortableData } from '../hooks/useSortableData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to export CSV with auth token
const exportWithAuth = async (endpoint, filename) => {
  const token = localStorage.getItem('token');
  try {
    const response = await fetch(`${API}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    
    // Detect Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // For IE/Edge legacy
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return;
    }
    
    // For Safari - use FileReader to convert to data URL
    if (isSafari) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const link = document.createElement('a');
        link.href = reader.result;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 250);
      };
      reader.readAsDataURL(blob);
      return;
    }
    
    // For Chrome, Firefox, Edge (Chromium)
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 250);
  } catch (error) {
    console.error('Export error:', error);
    throw error;
  }
};

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const { canManageCustomers, canDeleteCustomers, canViewPrices } = useAuth();
  const { formatCurrency } = useSettings();

  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company_name: '',
      address: '',
      city: '',
      phone: '',
      email: '',
      notes: ''
    });
    setEditingCustomer(null);
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name,
        company_name: customer.company_name || '',
        address: customer.address || '',
        city: customer.city || '',
        phone: customer.phone || '',
        email: customer.email || '',
        notes: customer.notes || ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API}/customers`, formData);
        toast.success('Customer created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/customers/${customerToDelete.id}`);
      toast.success('Customer deleted successfully');
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  // Filter customers by search term
  const searchFilteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.company_name && customer.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (customer.phone && customer.phone.includes(searchTerm))
  );

  // Apply sorting using the custom hook
  const { sortedData: filteredCustomers, sortBy, setSortBy, sortOptions } = useSortableData(searchFilteredCustomers);

  const downloadTemplate = () => {
    window.open(`${API}/customers/import/template`, '_blank');
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    const formDataObj = new FormData();
    formDataObj.append('file', importFile);
    
    try {
      const response = await axios.post(`${API}/customers/import`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { success_count, error_count, errors } = response.data;
      
      if (success_count > 0) {
        toast.success(`Successfully imported ${success_count} customers`);
        fetchCustomers();
      }
      
      if (error_count > 0) {
        toast.error(`${error_count} rows had errors`);
        errors.slice(0, 3).forEach(err => {
          toast.error(`Row ${err.row}: ${err.error}`);
        });
      }
      
      setImportDialogOpen(false);
      setImportFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Customers</h1>
          <p className="text-slate-500 mt-1">Manage your customer database</p>
        </div>
        {canManageCustomers() && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                const params = new URLSearchParams();
                if (searchTerm) params.append('search', searchTerm);
                try {
                  await exportWithAuth(`/customers/export?${params.toString()}`, 'customers_export.csv');
                  toast.success('Customers exported successfully');
                } catch (error) {
                  toast.error('Failed to export customers');
                }
              }}
              className="rounded-sm"
              data-testid="export-customers-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
              className="rounded-sm"
              data-testid="import-customers-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="add-customer-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          </div>
        )}
      </div>

      {/* Search and Sort */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search customers by name, company, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="customer-search"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-40 rounded-sm" data-testid="sort-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-0">
          {filteredCustomers.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Issuances</TableHead>
                  {canViewPrices() && <TableHead className="text-right">Total Cost</TableHead>}
                  {canViewPrices() && <TableHead className="text-right">Total Value</TableHead>}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map(customer => (
                  <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-slate-900">{customer.name}</p>
                        {customer.company_name && (
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {customer.company_name}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {customer.phone && (
                          <p className="text-sm flex items-center gap-1 text-slate-600">
                            <Phone className="w-3 h-3" />
                            {customer.phone}
                          </p>
                        )}
                        {customer.email && (
                          <p className="text-sm flex items-center gap-1 text-slate-600">
                            <Mail className="w-3 h-3" />
                            {customer.email}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(customer.address || customer.city) && (
                        <p className="text-sm flex items-center gap-1 text-slate-600">
                          <MapPin className="w-3 h-3" />
                          {[customer.address, customer.city].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary" className="rounded-sm">
                        {customer.total_issuances || 0}
                      </Badge>
                    </TableCell>
                    {canViewPrices() && (
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(customer.total_parts_cost || 0)}
                      </TableCell>
                    )}
                    {canViewPrices() && (
                      <TableCell className="text-right font-mono text-sm font-semibold text-emerald-600">
                        {formatCurrency(customer.total_parts_value || 0)}
                      </TableCell>
                    )}
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/customers/${customer.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View History
                          </DropdownMenuItem>
                          {canManageCustomers() && (
                            <DropdownMenuItem onClick={() => handleOpenDialog(customer)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {canDeleteCustomers() && customer.total_issuances === 0 && (
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setCustomerToDelete(customer);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Users className="w-12 h-12 mb-4" />
              <p className="text-lg font-medium">No customers found</p>
              <p className="text-sm">Add your first customer to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh]" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">
              {editingCustomer ? 'Edit Customer' : 'Add Customer'}
            </DialogTitle>
            <DialogDescription>
              {editingCustomer ? 'Update customer information' : 'Add a new customer to your database'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label htmlFor="name" className="text-xs sm:text-sm">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm h-9"
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label htmlFor="company_name" className="text-xs sm:text-sm">Company Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="rounded-sm h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-xs sm:text-sm">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-sm h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs sm:text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-sm h-9"
                />
              </div>
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label htmlFor="address" className="text-xs sm:text-sm">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="rounded-sm h-9"
                />
              </div>
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label htmlFor="city" className="text-xs sm:text-sm">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="rounded-sm h-9"
                />
              </div>
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label htmlFor="notes" className="text-xs sm:text-sm">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="rounded-sm"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm w-full sm:w-auto">
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm w-full sm:w-auto" data-testid="customer-submit-btn">
                {editingCustomer ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-red-600">Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{customerToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="rounded-sm w-full sm:w-auto" data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="import-customers-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold">Import Customers</DialogTitle>
            <DialogDescription>
              Upload a CSV file to import multiple customers at once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-sm border border-slate-200">
              <h4 className="font-medium text-sm mb-2">CSV Format</h4>
              <p className="text-xs text-slate-500 mb-3">
                Required: <span className="font-mono bg-slate-200 px-1">name</span><br />
                Optional: company_name, phone, email, address, city, notes
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="rounded-sm"
                data-testid="download-customer-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="importFile">Select CSV File</Label>
              <Input
                id="importFile"
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="rounded-sm"
                data-testid="customer-import-file-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setImportDialogOpen(false);
                setImportFile(null);
              }}
              className="rounded-sm w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm w-full sm:w-auto"
              data-testid="confirm-import-customers-btn"
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
